import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type {
    CreateVesselGroupRequest,
    UpdateVesselGroupRequest,
    VesselGroup,
    VesselGroupsRequest,
    VesselGroupsResponse,
} from '@routes/sidebar/admin-center/___shared___/api/vesselGroup.api.ts';
import {
    createApiVesselGroup,
    deleteApiVesselGroup,
    getApiVesselGroups,
    updateApiVesselGroup,
} from '@routes/sidebar/admin-center/___shared___/api/vesselGroup.api.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import { extractError } from '@utils/slice.ts';

export interface VesselGroupsError {
    message?: string;
    [k: string]: any;
}

export interface VesselGroupsState {
    error: VesselGroupsError | null;

    commonVesselGroups: VesselGroupsResponse | null;
    vesselGroups: VesselGroup[];
    pagination: CommonTablePaginatedResponse;

    updatedVesselGroup: VesselGroup | null;
    currentGetRequestId: string | null;

    isGetVesselGroupsPending: boolean;
    isCreateVesselGroupPending: boolean;
    isUpdateVesselGroupPending: boolean;
    isDeleteVesselGroupPending: boolean;
}

const initialState: VesselGroupsState = {
    error: null,

    commonVesselGroups: null,
    vesselGroups: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedVesselGroup: null,
    currentGetRequestId: null,

    isGetVesselGroupsPending: false,
    isCreateVesselGroupPending: false,
    isUpdateVesselGroupPending: false,
    isDeleteVesselGroupPending: false,
};

export const getVesselGroups = createAsyncThunk<
    VesselGroupsResponse,
    { clientId: number; tenantAlias: string; payload: VesselGroupsRequest },
    { rejectValue: VesselGroupsError }
>('adminCenterVesselGroups/getVesselGroups', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiVesselGroups(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createVesselGroup = createAsyncThunk<
    VesselGroup,
    { clientId: number; tenantAlias: string; payload: CreateVesselGroupRequest },
    { rejectValue: VesselGroupsError }
>('adminCenterVesselGroups/createVesselGroup', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiVesselGroup(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateVesselGroup = createAsyncThunk<
    VesselGroup,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateVesselGroupRequest },
    { rejectValue: VesselGroupsError }
>('adminCenterVesselGroups/updateVesselGroup', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiVesselGroup(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteVesselGroup = createAsyncThunk<
    VesselGroup,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: VesselGroupsError }
>('adminCenterVesselGroups/deleteVesselGroup', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiVesselGroup(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const vesselGroupsSlice = createSlice({
    name: 'adminCenterVesselGroups',
    initialState,
    reducers: {
        resetVesselGroupsState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getVesselGroups.pending, (state, action) => {
            state.isGetVesselGroupsPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getVesselGroups.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselGroupsPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonVesselGroups = action.payload;
            state.vesselGroups = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getVesselGroups.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselGroupsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createVesselGroup.pending, (state) => {
            state.isCreateVesselGroupPending = true;
            state.error = null;
        });
        builder.addCase(createVesselGroup.fulfilled, (state, action) => {
            state.isCreateVesselGroupPending = false;
            state.updatedVesselGroup = action.payload;
        });
        builder.addCase(createVesselGroup.rejected, (state, action) => {
            state.isCreateVesselGroupPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateVesselGroup.pending, (state) => {
            state.isUpdateVesselGroupPending = true;
            state.error = null;
        });
        builder.addCase(updateVesselGroup.fulfilled, (state, action) => {
            state.isUpdateVesselGroupPending = false;
            state.updatedVesselGroup = action.payload;
        });
        builder.addCase(updateVesselGroup.rejected, (state, action) => {
            state.isUpdateVesselGroupPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteVesselGroup.pending, (state) => {
            state.isDeleteVesselGroupPending = true;
            state.error = null;
        });
        builder.addCase(deleteVesselGroup.fulfilled, (state, action) => {
            state.isDeleteVesselGroupPending = false;
            state.updatedVesselGroup = action.payload;
            state.vesselGroups = state.vesselGroups.filter((g) => g.id !== action.payload.id);
        });
        builder.addCase(deleteVesselGroup.rejected, (state, action) => {
            state.isDeleteVesselGroupPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { vesselGroup } = action.payload;

            if (!vesselGroup) return;

            const { data, pagination } = vesselGroup;
            state.commonVesselGroups = vesselGroup;
            state.vesselGroups = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { resetVesselGroupsState } = vesselGroupsSlice.actions;
export default vesselGroupsSlice.reducer;
